import { ColumnFiltersState } from '@tanstack/react-table';
import { z } from 'zod';

import { getInconsistencySourceDisplayName } from '../../../backend/src/case/inconsistency/inconsistency-display';
import { capitalizeFirstLetter } from '../helpers/filterNames';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';
import { DocFilters, FactFilters } from '@/types';
import {
  getExtensionLabel,
  getDocumentTypeLabel,
  getRelevanceLabel,
  getClassificationLabel,
  getPersonLabel,
  getProcessedLabel,
  getDateLabel,
} from '@/utils/filterLookups';

// Zod schema for date range filter values
const DateRangeFilterSchema = z.object({
  startDate: z.date().nullable(),
  endDate: z.date().nullable(),
});

export type DateRangeFilterValue = z.infer<typeof DateRangeFilterSchema>;

export interface FilterPill {
  id: string;
  label: string;
  color: string;
  onRemove?: () => void;
  nonVisibleItems?: string[];
  icon?: string; // Lucide icon class name (e.g., 'lucide--database')
}

// TODO: move these colours to somewhere sensible
// Color palette
export const ATTRIBUTE_COLOURS = {
  date: 'bg-orange-100 text-orange-800 border-orange-300',
  theme: 'bg-blue-50 text-blue-600 border-blue-300',
  processed: 'bg-gray-100 text-gray-700 border-gray-300',
  extension: 'bg-gray-100 text-gray-700 border-gray-300',
  docType: 'bg-brandTertiary/10 text-brandTertiary border-brandTertiary/30',
  hasKeyFacts: 'bg-yellow-50 text-yellow-600 border-yellow-300',
  hasRelevantFacts: 'bg-green-50 text-green-600 border-green-300',
  relevance: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  classification: 'bg-red-100 text-red-800 border-red-300',
  person: 'bg-teal-100 text-teal-800 border-teal-300',
  keyFacts: 'bg-amber-100 text-amber-800 border-amber-300',
  disputedFacts: 'bg-rose-100 text-rose-800 border-rose-300',
  document: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  excludedDocument: 'bg-gray-100 text-gray-700 border-gray-300',
  searchQuery: 'bg-gray-100 text-gray-700 border-gray-300',
  relativityMetadata: 'bg-purple-100 text-purple-700 border-purple-300',
  kimThread: 'bg-teal-50 text-teal-600 border-teal-200',
};

// Shared filter pill builders
function addDateRangePills(
  pills: FilterPill[],
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  onRemoveFilter: (key: string) => void,
) {
  if (startDate) {
    pills.push({
      id: 'startDate',
      label: getDateLabel(startDate, 'From'),
      color: ATTRIBUTE_COLOURS.date,
      onRemove: () => onRemoveFilter('startDate'),
    });
  }

  if (endDate) {
    pills.push({
      id: 'endDate',
      label: getDateLabel(endDate, 'To'),
      color: ATTRIBUTE_COLOURS.date,
      onRemove: () => onRemoveFilter('endDate'),
    });
  }
}

function addDocumentTypesPills(
  pills: FilterPill[],
  documentTypes: string[] | undefined,
  onRemoveFilter: (key: string, value: string) => void,
) {
  if (documentTypes?.length) {
    const maxVisible = 3;
    const visibleTypes = documentTypes.slice(0, maxVisible);
    const remainingCount = documentTypes.length - maxVisible;

    visibleTypes.forEach((type, index) => {
      pills.push({
        id: `docType-${type}-${index}`,
        label: getDocumentTypeLabel(type),
        color: ATTRIBUTE_COLOURS.docType,
        onRemove: () => onRemoveFilter('documentTypes', type),
      });
    });

    if (remainingCount > 0) {
      pills.push({
        id: 'docType-more',
        label: `+${remainingCount} more`,
        color: ATTRIBUTE_COLOURS.docType,
        nonVisibleItems: documentTypes.slice(maxVisible),
      });
    }
  }
}

function addThemesPills(
  pills: FilterPill[],
  themes: string[] | undefined,
  filterKey: string,
  onRemoveFilter: (key: string, value: string) => void,
) {
  if (themes?.length) {
    themes.forEach((theme, index) => {
      pills.push({
        id: `theme-${theme}-${index}`,
        label: theme,
        color: ATTRIBUTE_COLOURS.theme,
        onRemove: () => onRemoveFilter(filterKey, theme),
      });
    });
  }
}

export function buildDocumentFilterPills(
  filters: DocFilters,
  onRemoveFilter: (key: string, value?: any) => void,
  relativityFieldDisplayNames?: Record<string, string>,
): FilterPill[] {
  const pills: FilterPill[] = [];

  // Processed status
  if (filters.processed !== null && filters.processed !== undefined) {
    pills.push({
      id: 'processed',
      label: getProcessedLabel(filters.processed),
      color: ATTRIBUTE_COLOURS.processed,
      onRemove: () => onRemoveFilter('processed'),
    });
  }

  // Extensions (file types)
  if (filters.extensions?.length) {
    filters.extensions.forEach((ext, index) => {
      pills.push({
        id: `extension-${ext}-${index}`,
        label: getExtensionLabel(ext),
        color: ATTRIBUTE_COLOURS.extension,
        onRemove: () => onRemoveFilter('extensions', ext),
      });
    });
  }

  addDocumentTypesPills(pills, filters.documentTypes, onRemoveFilter);
  addThemesPills(pills, filters.documentThemes, 'documentThemes', onRemoveFilter);
  addDateRangePills(pills, filters.startDate, filters.endDate, onRemoveFilter);

  // Boolean flags
  if (filters.hasKeyFacts) {
    pills.push({
      id: 'hasKeyFacts',
      label: 'Contains Key Facts',
      color: ATTRIBUTE_COLOURS.hasKeyFacts,
      onRemove: () => onRemoveFilter('hasKeyFacts'),
    });
  }

  if (filters.hasRelevantFacts) {
    pills.push({
      id: 'hasRelevantFacts',
      label: 'Contains Relevant Facts',
      color: ATTRIBUTE_COLOURS.hasRelevantFacts,
      onRemove: () => onRemoveFilter('hasRelevantFacts'),
    });
  }

  // Relativity metadata filters
  if (filters.metadataFilters?.length) {
    filters.metadataFilters.forEach((filter, index) => {
      const valueDisplay = Array.isArray(filter.value)
        ? filter.value.length > 2
          ? `${filter.value.length} selected`
          : filter.value.join(', ')
        : String(filter.value);

      // Use display name from lookup if available, otherwise use field name
      const fieldDisplayName = relativityFieldDisplayNames?.[filter.field] ?? filter.field;

      pills.push({
        id: `metadataFilter-${index}`,
        label: `${fieldDisplayName}: ${valueDisplay}`,
        color: ATTRIBUTE_COLOURS.relativityMetadata,
        icon: 'lucide--database',
        onRemove: () => onRemoveFilter('metadataFilters', index),
      });
    });
  }

  return pills;
}

export function buildFactFilterPills(
  filters: FactFilters,
  onRemoveFilter: (key: string, value?: any) => void,
  personsMap?: Map<string, string>,
): FilterPill[] {
  const pills: FilterPill[] = [];

  // Keyword search query
  if (filters.searchQuery && filters.searchQuery.trim()) {
    pills.push({
      id: 'searchQuery',
      label: `"${filters.searchQuery}"`,
      color: ATTRIBUTE_COLOURS.searchQuery,
      onRemove: () => onRemoveFilter('searchQuery'),
    });
  }

  // Key facts filter
  if (filters.keyFacts) {
    pills.push({
      id: 'keyFacts',
      label: 'Key Facts',
      color: ATTRIBUTE_COLOURS.keyFacts,
      onRemove: () => onRemoveFilter('keyFacts'),
    });
  }

  // Disputed facts filter
  if (filters.disputedFacts) {
    pills.push({
      id: 'disputedFacts',
      label: 'Disputed Facts',
      color: ATTRIBUTE_COLOURS.disputedFacts,
      onRemove: () => onRemoveFilter('disputedFacts'),
    });
  }

  // Included documents
  if (filters.docIds?.length) {
    const docCount = filters.docIds.filter((doc) => doc).length;
    pills.push({
      id: 'docIds',
      label: `${docCount} ${docCount === 1 ? 'doc' : 'docs'}`,
      color: ATTRIBUTE_COLOURS.document,
      onRemove: () => onRemoveFilter('docIds'),
    });
  }

  // Excluded documents
  if (filters.excludedDocIds?.length) {
    const excludedCount = filters.excludedDocIds.filter((doc) => doc).length;
    pills.push({
      id: 'excludedDocIds',
      label: `${excludedCount} excluded ${excludedCount === 1 ? 'doc' : 'docs'}`,
      color: ATTRIBUTE_COLOURS.excludedDocument,
      onRemove: () => onRemoveFilter('excludedDocIds'),
    });
  }

  // Relevance levels
  if (filters.relevance?.length) {
    filters.relevance.forEach((rel, index) => {
      pills.push({
        id: `relevance-${rel}-${index}`,
        label: getRelevanceLabel(rel),
        color: ATTRIBUTE_COLOURS.relevance,
        onRemove: () => onRemoveFilter('relevance', rel),
      });
    });
  }

  // Classifications
  if (filters.classification?.length) {
    filters.classification.forEach((cls, index) => {
      pills.push({
        id: `classification-${cls}-${index}`,
        label: getClassificationLabel(cls),
        color: ATTRIBUTE_COLOURS.classification,
        onRemove: () => onRemoveFilter('classification', cls),
      });
    });
  }

  addDocumentTypesPills(pills, filters.documentTypes, onRemoveFilter);
  addThemesPills(pills, filters.themes, 'themes', onRemoveFilter);
  addDateRangePills(pills, filters.startDate, filters.endDate, onRemoveFilter);

  // Persons/entities
  if (filters.personIds?.length) {
    filters.personIds.forEach((personId, index) => {
      pills.push({
        id: `person-${personId}-${index}`,
        label: getPersonLabel(personId, personsMap),
        color: ATTRIBUTE_COLOURS.person,
        onRemove: () => onRemoveFilter('personIds', personId),
      });
    });
  }

  // Thread filter (applied via URL param only)
  if (filters.threadId) {
    pills.push({
      id: 'threadId',
      label: 'Kim References',
      color: ATTRIBUTE_COLOURS.kimThread,
      onRemove: () => onRemoveFilter('threadId'),
    });
  }

  return pills;
}

export function buildFilterPills(
  filters: DocFilters | FactFilters,
  entityType: 'document' | 'fact',
  onRemoveFilter: (key: string, value?: any) => void,
  personsMap?: Map<string, string>,
): FilterPill[] {
  if (entityType === 'document') {
    return buildDocumentFilterPills(filters as DocFilters, onRemoveFilter);
  } else {
    return buildFactFilterPills(filters as FactFilters, onRemoveFilter, personsMap);
  }
}

export function buildClientSideFilterPills(
  columnFilters: ColumnFiltersState,
  setColumnFilters: (filters: ColumnFiltersState | ((prev: ColumnFiltersState) => ColumnFiltersState)) => void,
): FilterPill[] {
  const pills: FilterPill[] = [];

  columnFilters.forEach((filter) => {
    const columnId = filter.id;
    const filterValue = filter.value;

    // Handle date range filters
    if (columnId === 'date') {
      const parseResult = DateRangeFilterSchema.safeParse(filterValue);
      if (!parseResult.success) return;

      const { startDate, endDate } = parseResult.data;

      const createDateRemoveHandler = (field: 'startDate' | 'endDate') => () => {
        setColumnFilters((prev) =>
          prev
            .map((f) =>
              f.id === 'date' ? { ...f, value: { ...(f.value as DateRangeFilterValue), [field]: null } } : f,
            )
            .filter((f) => {
              if (f.id !== 'date') return true;
              const dateVal = f.value as DateRangeFilterValue;
              return dateVal.startDate || dateVal.endDate;
            }),
        );
      };

      if (startDate) {
        pills.push({
          id: 'startDate',
          label: getDateLabel(startDate.toISOString(), 'From'),
          color: ATTRIBUTE_COLOURS.date,
          onRemove: createDateRemoveHandler('startDate'),
        });
      }

      if (endDate) {
        pills.push({
          id: 'endDate',
          label: getDateLabel(endDate.toISOString(), 'To'),
          color: ATTRIBUTE_COLOURS.date,
          onRemove: createDateRemoveHandler('endDate'),
        });
      }

      return;
    }

    // Handle array-based filter values (from multi-select)
    if (Array.isArray(filterValue) && filterValue.length > 0) {
      filterValue.forEach((value, index) => {
        let label = value;
        let color = CHIP_COLOURS.extension;

        // Customize label and color based on column type
        if (columnId === 'type') {
          label = getDocumentTypeLabel(value);
          color = CHIP_COLOURS.type;
        } else if (columnId === 'file.extension') {
          label = getExtensionLabel(value);
          color = CHIP_COLOURS.extension;
        } else if (columnId === 'issues') {
          label = value;
          color = CHIP_COLOURS.issue;
        } else if (columnId === 'file.fileMetadata.relativityFields.issues') {
          label = `Rel. Issue: ${value}`;
          color = ATTRIBUTE_COLOURS.relativityMetadata;
        } else if (columnId === 'file.createdDate') {
          label = `Run at ${value}`;
          color = ATTRIBUTE_COLOURS.date;
        } else if (columnId === 'persons') {
          label = value;
          color = ATTRIBUTE_COLOURS.person;
        } else if (columnId === 'level') {
          label = capitalizeFirstLetter(value);
          color = CHIP_COLOURS.levels[value as keyof typeof CHIP_COLOURS.levels] || CHIP_COLOURS.extension;
        } else if (columnId === 'source') {
          label = getInconsistencySourceDisplayName(value);
          color = CHIP_COLOURS.inconsistencySource;
        }

        pills.push({
          id: `${columnId}-${value}-${index}`,
          label,
          color,
          onRemove: () => {
            // Remove this specific value from the filter
            const updatedFilters = columnFilters
              .map((f) => {
                if (f.id === columnId) {
                  const newValues = (f.value as string[]).filter((v) => v !== value);
                  return newValues.length > 0 ? { ...f, value: newValues } : null;
                }
                return f;
              })
              .filter((f): f is { id: string; value: any } => f !== null);

            setColumnFilters(updatedFilters);
          },
        });
      });
    }
  });

  return pills;
}
